import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@truecare/ui';

/**
 * Phase 14b-1-1 — `<AdminTable>` shared table component.
 *
 * Plain object array column-definition API per the lock. TS generics on
 * `<AdminTable<T>>` carry the row shape into each column's `render`. Matches
 * MUI / Antd / Stripe Dashboard precedent.
 *
 * Per 14b-1-2 — every column is sortable by default; columns set
 * `sortable: false` to opt out (e.g., computed columns that can't be
 * sorted server-side). Click toggles asc → desc → cleared (no-sort).
 *
 * Per 14b-1-7 — pagination footer renders Prev/Next + a "Page N of M"
 * indicator + optional page-size selector (25/50/100). Operator is
 * expected to narrow with filters BEFORE paging at v1 scale; page-number
 * jump is a v1.x candidate if pilot operators scrub many pages.
 *
 * Per 14-C density expectation — compact row height, visible borders,
 * dense font sizing. Editorial breathing room stays in the page-level
 * surrounding chrome.
 */
export interface AdminTableColumn<T> {
  /** Column identifier; used as React key + as the sort-key sent to
   *  the server when this column is clicked for sorting. */
  key: keyof T | string;
  header: string;
  /** Custom renderer. Defaults to `String(row[key])`. */
  render?: (row: T) => ReactNode;
  /** Defaults to true; set false to disable click-to-sort for this
   *  column (computed columns, multi-field aggregates, etc.). */
  sortable?: boolean;
  /** Optional Tailwind width class (e.g., 'w-24', 'w-1/3'). */
  width?: string;
}

export interface AdminTableProps<T extends { id: string }> {
  columns: AdminTableColumn<T>[];
  rows: T[];
  isLoading?: boolean;
  isError?: boolean;
  /** When provided, the error state shows a Retry button that calls this (e.g. query.refetch). */
  onRetry?: () => void;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  // Pagination per 14b-1-7
  page: number;
  limit: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  // Sorting per 14b-1-2
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (key: string, order: 'asc' | 'desc') => void;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export function AdminTable<T extends { id: string }>(props: AdminTableProps<T>) {
  const {
    columns,
    rows,
    isLoading = false,
    isError = false,
    onRetry,
    emptyMessage = 'No results.',
    onRowClick,
    page,
    limit,
    totalCount,
    onPageChange,
    onLimitChange,
    sortKey,
    sortOrder,
    onSortChange,
  } = props;

  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, limit)));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // Horizontal-scroll affordance (Phase 17 M2). On a phone the fixed column
  // widths push the status/decision columns off the right edge with no hint
  // that the table scrolls. A subtle edge-fade on each scrollable side signals
  // "more content this way" and disappears once that edge is reached. Desktop —
  // where the table fits — shows neither fade, so it stays visually unchanged.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  // The empty / error message must fill the VISIBLE table area and stay pinned to
  // its left on horizontal scroll — but it must NOT widen the table. `w-screen`
  // (100vw) did the mobile job but blew the desktop table out to 100vw (wider
  // than its card), spilling the header past the card. Size the message to the
  // scroll container's measured client width instead.
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const measureScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setShowLeftFade(scrollLeft > 1);
    setShowRightFade(scrollLeft + clientWidth < scrollWidth - 1);
    setContainerWidth(clientWidth);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    measureScroll();
    el.addEventListener('scroll', measureScroll, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureScroll) : null;
    ro?.observe(el);
    window.addEventListener('resize', measureScroll);
    return () => {
      el.removeEventListener('scroll', measureScroll);
      ro?.disconnect();
      window.removeEventListener('resize', measureScroll);
    };
  }, [measureScroll]);

  // Re-measure when the rendered content changes (row set / loading / error)
  // — content-width changes don't resize the scroll container itself, so the
  // ResizeObserver alone wouldn't catch them.
  useEffect(() => {
    measureScroll();
  }, [measureScroll, rows, isLoading, isError]);

  function handleHeaderClick(col: AdminTableColumn<T>): void {
    if (col.sortable === false) return;
    if (!onSortChange) return;
    const k = String(col.key);
    // asc → desc → asc (don't fully clear at v1 — keeps server state
    // predictable; clearing is a v1.x candidate if pilot reveals need)
    if (sortKey !== k) {
      onSortChange(k, 'asc');
      return;
    }
    onSortChange(k, sortOrder === 'asc' ? 'desc' : 'asc');
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-paper">
      <div className="relative">
        <div ref={scrollRef} className="overflow-x-auto" data-testid="admin-table-scroll">
        <table className="min-w-full divide-y divide-border text-sm" data-testid="admin-table">
          <thead className="bg-paper-warm">
            <tr>
              {columns.map((col) => {
                const k = String(col.key);
                const isSorted = sortKey === k;
                const sortable = col.sortable !== false && Boolean(onSortChange);
                return (
                  <th
                    key={k}
                    scope="col"
                    className={[
                      'px-3 py-2 text-left font-body font-medium text-forest',
                      'border-r border-border last:border-r-0',
                      col.width ?? '',
                      sortable ? 'cursor-pointer hover:bg-forest/5 select-none' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={sortable ? () => handleHeaderClick(col) : undefined}
                    data-testid={`admin-table-header-${k}`}
                    data-sortable={sortable ? 'true' : 'false'}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {isSorted && (
                        <span aria-hidden className="text-ink-muted">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={`skeleton-${i}`} data-testid="admin-table-skeleton-row">
                  {columns.map((col, j) => (
                    <td
                      key={`s-${i}-${j}`}
                      className="px-3 py-3 border-r border-border last:border-r-0"
                    >
                      <span className="block h-3 w-full animate-pulse rounded bg-ink/10" />
                    </td>
                  ))}
                </tr>
              ))
            ) : isError ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="p-0"
                  data-testid="admin-table-error"
                >
                  {/* The header row's fixed column widths can make the table
                      wider than the viewport; a plain centered cell pushes the
                      Retry action off-screen behind the horizontal scroll. Pin
                      the message to the scroll area's left edge (sticky) and size
                      it to the container's client width so Retry is always
                      reachable AND the table isn't forced wider than its card. */}
                  <div
                    className="sticky left-0 px-3 py-6 text-center text-sm text-danger"
                    style={containerWidth ? { width: `${containerWidth}px` } : undefined}
                  >
                    Couldn't load this list.{' '}
                    {onRetry ? (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="font-semibold text-forest underline underline-offset-2 hover:text-forest-dark"
                      >
                        Retry
                      </button>
                    ) : (
                      'Refresh the page to try again.'
                    )}
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="p-0"
                  data-testid="admin-table-empty"
                >
                  {/* Sized to the scroll container's client width + pinned left
                      so the message stays centred + readable on mobile without
                      forcing the table wider than its card (see error state). */}
                  <div
                    className="sticky left-0 px-3 py-6 text-center text-sm text-ink-muted"
                    style={containerWidth ? { width: `${containerWidth}px` } : undefined}
                  >
                    {emptyMessage}
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={[
                    // §6.1 admin density calibration (text-sm leading-snug).
                    'admin-list-row',
                    onRowClick
                      ? 'cursor-pointer hover:bg-forest/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-forest'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  // Keyboard equivalent of the row click (WCAG 2.1.1) — a bare
                  // <tr onClick> is mouse-only. Enter/Space activate; the row is
                  // focusable and exposed as a button to assistive tech.
                  //
                  // H14 — scope to `e.target === e.currentTarget`: Enter/Space
                  // bubbles from nested action buttons (Void / Refund / Record
                  // payment) up to the row. Without this guard a keyboard
                  // operator tabbing to a row button and pressing Enter fired
                  // BOTH the button and the row-detail modal (and the
                  // preventDefault suppressed the button's own activation). Only
                  // act when the row itself is the focused target.
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row);
                          }
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  data-testid={`admin-table-row-${row.id}`}
                >
                  {columns.map((col) => {
                    const k = String(col.key);
                    return (
                      <td
                        key={k}
                        className="px-3 py-1.5 border-r border-border last:border-r-0 align-top"
                      >
                        {col.render
                          ? col.render(row)
                          : renderDefault(row, col.key as keyof T)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {showLeftFade && (
          <div
            aria-hidden
            data-testid="admin-table-scroll-fade-left"
            className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-ink/10 to-transparent"
          />
        )}
        {showRightFade && (
          <div
            aria-hidden
            data-testid="admin-table-scroll-fade-right"
            className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-ink/10 to-transparent"
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-paper-warm px-3 py-2 text-sm text-ink-muted">
        <div data-testid="admin-table-page-indicator">
          Page {page} of {totalPages} · {totalCount} total
        </div>
        <div className="flex items-center gap-3">
          {onLimitChange && (
            <label className="flex items-center gap-2">
              <span>Rows per page</span>
              <select
                className="rounded-sm border border-border bg-white px-2 py-1 text-forest"
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                data-testid="admin-table-limit-select"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={!canPrev}
            onClick={() => canPrev && onPageChange(page - 1)}
            data-testid="admin-table-prev"
          >
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!canNext}
            onClick={() => canNext && onPageChange(page + 1)}
            data-testid="admin-table-next"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

function renderDefault<T>(row: T, key: keyof T): ReactNode {
  const value = row[key];
  if (value === null || value === undefined) return '—';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
